// ============================================================
// NotificationBell.js
// Bell + unread badge + dropdown panel for the `notifications` collection.
//
// Lives in the SIDEBAR rather than a page header. Every page renders its own
// header — there are fifteen of them — but they all render <Sidebar />, so this
// is the one place the bell can go and be present everywhere.
//
// This component holds the app's FIRST Firestore onSnapshot listener.
// Everything else in the codebase is one-shot getDocs plus a refresh button,
// which is fine for a price list and useless for a notification: a bell that
// only updates when you reload is not a bell. The listener is deliberately the
// narrowest possible — one collection, one equality filter, no ordering — and
// its teardown is handled below.
// ============================================================

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

// App colour tokens, same values the sidebar and the pickers use.
const C_CARD = '#FFFFFF';
const C_BORDER = '#E3ECF5';
const C_TEXT = '#03045E';
const C_TEXT_2 = '#6B7C93';
const C_TEXT_3 = '#9AA8BC';
const C_ACCENT = '#0077B6';
const C_TINT = '#E6F1FB';
const C_RED = '#A32D2D';

// How many rows the panel shows. Beyond this the panel says how many more
// there are rather than growing without limit.
const MAX_VISIBLE = 20;

/**
 * "5m ago" / "2h ago" / "Yesterday" / "3d ago", falling back to a plain date
 * once a notification is a week old — past that, how long ago stops being the
 * useful fact and the date is.
 */
export function relativeTime(iso, now = Date.now()) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** What happened, in words. Unknown types degrade to something readable. */
export const describeType = (type) =>
  type === 'mention' ? 'mentioned you'
    : type === 'comment' ? 'commented on your invoice'
      : 'left you a note';

/** Newest first. createdAt is an ISO string, so lexical order is chronological. */
export const sortNewestFirst = (list) =>
  [...list].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

export default function NotificationBell({ displayName }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // ── The listener ──
  //
  // query: one collection, ONE equality filter, and no orderBy. Firestore only
  // demands a composite index when a query combines filters and ordering, so
  // this one runs on the automatic single-field index — nothing to deploy.
  // Ordering is done client-side below, the same trade CustomerProfile makes
  // for its shopDrawings query.
  //
  // Teardown: onSnapshot returns its own unsubscribe function, and returning it
  // straight out of the effect makes React call it BOTH on unmount and before
  // the effect re-runs — which is what displayName changing does. A listener
  // left open would keep reading Firestore after logout, under the previous
  // user's name, for as long as the tab stayed alive.
  useEffect(() => {
    // Before auth resolves displayName is undefined, and a where() clause
    // cannot take undefined — it throws. There is also nothing to listen for
    // yet, so the guard is correctness as well as safety.
    if (!displayName) {
      setItems([]);
      return undefined;
    }

    const unsubscribe = onSnapshot(
      query(collection(db, 'notifications'), where('recipient', '==', displayName)),
      (snap) => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      (err) => {
        // A failed listener must not take the sidebar down with it.
        console.error('Notification listener failed:', err);
        setItems([]);
      },
    );

    return unsubscribe;
  }, [displayName]);

  const sorted = useMemo(() => sortNewestFirst(items), [items]);
  const unreadCount = useMemo(() => items.filter(n => !n.readAt).length, [items]);
  const visible = sorted.slice(0, MAX_VISIBLE);
  const hiddenCount = sorted.length - visible.length;

  // ── Dismissal ──
  // Bound only while the panel is open, and removed on close, so there is no
  // permanent document-level listener sitting behind a closed panel.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  // Read state is a TIMESTAMP, never a deletion — a notification stays in the
  // panel after it has been read. No local state is touched here: the listener
  // is already watching this document and pushes the change straight back, so
  // an optimistic update would only risk disagreeing with the server.
  const markRead = async (n) => {
    if (!n || n.readAt) return;
    try {
      await updateDoc(doc(db, 'notifications', n.id), { readAt: new Date().toISOString() });
    } catch (err) {
      console.error('Could not mark notification read:', err);
    }
  };

  const markAllRead = async () => {
    const unread = items.filter(n => !n.readAt);
    if (unread.length === 0) return;
    const stamp = new Date().toISOString();
    try {
      await Promise.all(unread.map(n => updateDoc(doc(db, 'notifications', n.id), { readAt: stamp })));
    } catch (err) {
      console.error('Could not mark notifications read:', err);
    }
  };

  // The Dashboard owns the card modal, so this hands it the id in navigation
  // state and lets it open the card — the same handoff CustomerProfile uses to
  // open a specific drawing in ShopDrawings.
  const openNotification = (n) => {
    setOpen(false);
    markRead(n);
    navigate('/dashboard', { state: { invoiceId: n.invoiceId } });
  };

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        title={unreadCount > 0 ? `${unreadCount} unread` : 'Notifications'}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 8,
          background: open ? C_TINT : 'transparent',
          border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1,
          fontFamily: 'inherit', padding: 0,
        }}
        onMouseEnter={e => { e.currentTarget.style.background = C_TINT; }}
        onMouseLeave={e => { e.currentTarget.style.background = open ? C_TINT : 'transparent'; }}
      >
        <span role="img" aria-hidden="true">🔔</span>

        {/* No badge at zero — with nothing waiting, the sidebar reads exactly as
            it did before this component existed. */}
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: -2, right: -2,
            minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 8, background: C_RED, color: '#FFFFFF',
            fontSize: 10, fontWeight: 700, lineHeight: '16px', textAlign: 'center',
            border: `1px solid ${C_CARD}`, boxSizing: 'border-box',
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Same treatment as the customer picker and the mention picker: absolute
          panel, hairline border, 12px radius, the shared soft shadow, scrolling
          list of left-aligned full-width buttons. It opens UPWARD because the
          user block sits at the bottom of the sidebar. */}
      {open && (
        <div style={{
          position: 'absolute', zIndex: 200, bottom: '100%', left: 0, marginBottom: 8,
          width: 330, maxHeight: 420, display: 'flex', flexDirection: 'column',
          background: C_CARD, border: `1px solid ${C_BORDER}`, borderRadius: 12,
          boxShadow: '0 12px 30px rgba(3, 4, 94, 0.12)', overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 14px', borderBottom: `1px solid ${C_BORDER}`, flexShrink: 0,
          }}>
            <span style={{ color: C_TEXT, fontWeight: 700, fontSize: 13 }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                style={{
                  background: 'transparent', border: 'none', padding: 0,
                  color: C_ACCENT, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto' }}>
            {visible.length === 0 ? (
              <p style={{ padding: '18px 16px', color: C_TEXT_2, margin: 0, fontSize: 13, textAlign: 'center' }}>
                No notifications
              </p>
            ) : visible.map(n => {
              const unread = !n.readAt;
              return (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => openNotification(n)}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    // Unread rows are tinted and carry a dot; read rows sit flat
                    // with muted text, so the two are separable at a glance.
                    background: unread ? C_TINT : 'transparent',
                    border: 'none', borderBottom: `1px solid ${C_BORDER}`, padding: '10px 14px',
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = unread ? '#DCEBF8' : C_TINT; }}
                  onMouseLeave={e => { e.currentTarget.style.background = unread ? C_TINT : 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    {unread && (
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: C_ACCENT, flexShrink: 0 }} />
                    )}
                    <span style={{ color: unread ? C_TEXT : C_TEXT_2, fontWeight: 700, fontSize: 13 }}>
                      {n.actor}
                    </span>
                    <span style={{ color: C_TEXT_2, fontSize: 12 }}>{describeType(n.type)}</span>
                    <span style={{ color: C_TEXT_3, fontSize: 11, marginLeft: 'auto', flexShrink: 0 }}>
                      {relativeTime(n.createdAt)}
                    </span>
                  </div>

                  <p style={{ margin: '0 0 2px 0', color: C_ACCENT, fontSize: 11, fontWeight: 600 }}>
                    {[n.docNumber, n.customerName].filter(Boolean).join(' · ')}
                  </p>

                  {/* Two lines, then an ellipsis — a comment can be any length
                      and the panel is a summary, not the comment thread. */}
                  <p style={{
                    margin: 0, color: unread ? C_TEXT : C_TEXT_2, fontSize: 12, lineHeight: 1.4,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {n.text}
                  </p>
                </button>
              );
            })}
          </div>

          {hiddenCount > 0 && (
            <p style={{
              margin: 0, padding: '8px 14px', flexShrink: 0,
              borderTop: `1px solid ${C_BORDER}`,
              color: C_TEXT_2, fontSize: 11, textAlign: 'center',
            }}>
              Showing the {MAX_VISIBLE} most recent · {hiddenCount} older not shown
            </p>
          )}
        </div>
      )}
    </div>
  );
}
